import React, { useEffect, useState, useRef } from "react";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { usePhoneBar } from "@/contexts/PhoneBarContext";

interface TrustBadgesProps {
  cityName?: string;
}

export const TrustBadges = ({ cityName }: TrustBadgesProps = {}) => {
  const badges = [
    { name: "GAF Certified", logo: "/assets/badges/iko-badge.png" },
    { name: "Owens Corning Preferred", logo: "/assets/badges/gaf-master-elite-badge.png" },
    { name: "CertainTeed", logo: "/assets/badges/owens-corning-badge.png" },
    { name: "Johns Manville", logo: "/assets/badges/johns-manville-badge.png" },
    { name: "TAMKO PRO Gold", logo: "/assets/badges/tamko-pro-gold-badge.png" },
    { name: "BBB A+ Rated", logo: "/assets/badges/certainteed-badge.png" }
  ];

  const [api, setApi] = useState<CarouselApi>();
  const sectionRef = useRef<HTMLElement>(null);
  const { setIsVisible } = usePhoneBar();

  useEffect(() => {
    if (!api) return;

    const interval = setInterval(() => {
      api.scrollNext();
    }, 3000);

    return () => clearInterval(interval);
  }, [api]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50% 0px'
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [setIsVisible]);

  return (
    <section ref={sectionRef} className="py-16 bg-background border-y border-border/50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h3 className="text-2xl font-bold text-foreground mb-4">
            Trusted by Leading Manufacturers
          </h3>
          <p className="text-muted-foreground">
            We're certified and preferred contractors with the industry's top brands
          </p>
        </div>

        {/* Trust Badges Carousel */}
        <div className="max-w-4xl mx-auto">
          <Carousel
            setApi={setApi}
            opts={{
              align: "start",
              loop: true,
              slidesToScroll: 1,
              containScroll: "trimSnaps"
            }}
            className="w-full"
          >
            <CarouselContent className="-ml-4">
              {badges.map((badge, index) => (
                <CarouselItem key={index} className="pl-4 basis-2/3 md:basis-1/2 lg:basis-2/5">
                  <div className="flex items-center justify-center p-2 rounded-lg hover:bg-muted/50 transition-all duration-200 hover:scale-105 h-24 md:h-32 lg:h-40">
                    <img 
                      src={badge.logo} 
                      alt={`${badge.name} certification logo`}
                      className="max-w-full max-h-full object-contain filter hover:brightness-110 transition-all duration-200"
                      loading="lazy"
                      width="200"
                      height="160"
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </div>

        {/* Professional Credentials */}
        <div className="mt-16">
          <div className="text-center mb-8">
            <h4 className="text-xl font-bold text-foreground mb-2">
              Licensed & Insured Contractor
            </h4>
            <p className="text-muted-foreground">
              Serving Nassau & Suffolk Counties with Full Licensing & Insurance Coverage
            </p>
          </div>
          
          <div className="max-w-2xl mx-auto">
            <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="text-center space-y-2">
                  <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-full mx-auto mb-3">
                    <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h5 className="font-bold text-foreground text-lg">NASSAU COUNTY</h5>
                  <p className="text-primary font-semibold">LIC. # H3309480000</p>
                  <p className="text-muted-foreground text-sm">Licensed & Insured</p>
                </div>
                
                <div className="text-center space-y-2">
                  <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-full mx-auto mb-3">
                    <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h5 className="font-bold text-foreground text-lg">SUFFOLK COUNTY</h5>
                  <p className="text-primary font-semibold">LIC. # 45104-H</p>
                  <p className="text-muted-foreground text-sm">Licensed & Insured</p>
                </div>
              </div>
              
              <div className="mt-6 pt-6 border-t border-border text-center">
                <p className="text-sm text-muted-foreground">
                  Fully bonded and insured for your protection and peace of mind
                </p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};